import { useState, useCallback } from "react";
import { format, isAfter, isBefore } from "date-fns";

/**
 * Hook personalizado para gestionar el formulario de solicitud de bid
 * @returns {Object} Estado y funciones del formulario
 */
export const useBidForm = () => {
  // Estado del formulario
  const [formData, setFormData] = useState({
    checkIn: null,
    checkOut: null,
    adults: 1,
    children: 0,
    childrenAges: [],
  });

  const [errors, setErrors] = useState({});

  /**
   * Actualiza el rango de fechas
   */
  const setDateRange = useCallback((range) => {
    if (!range) {
      setFormData((prev) => ({ ...prev, checkIn: null, checkOut: null }));
      return;
    }

    const { from, to } = range;
    setFormData((prev) => ({
      ...prev,
      checkIn: from,
      checkOut: to,
    }));

    // Limpiar errores de fechas si se seleccionan correctamente
    if (from && to) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.checkIn;
        delete newErrors.checkOut;
        return newErrors;
      });
    }
  }, []);

  /**
   * Actualiza el número de adultos
   */
  const setAdults = useCallback((count) => {
    const adultsCount = Math.max(1, Math.min(10, parseInt(count) || 1));
    setFormData((prev) => ({ ...prev, adults: adultsCount }));
  }, []);

  /**
   * Actualiza el número de niños
   */
  const setChildren = useCallback((count) => {
    const childrenCount = Math.max(0, Math.min(10, parseInt(count) || 0));
    setFormData((prev) => ({
      ...prev,
      children: childrenCount,
      childrenAges:
        childrenCount > 0
          ? Array(childrenCount)
              .fill(null)
              .map(
                (_, i) => prev.childrenAges[i] || { edad: 0, unidad: "años" },
              )
          : [],
    }));
  }, []);

  /**
   * Actualiza la edad de un niño específico
   */
  const setChildAge = useCallback((index, edad, unidad = "años") => {
    setFormData((prev) => {
      const newAges = [...prev.childrenAges];
      newAges[index] = { edad: parseInt(edad) || 0, unidad };

      // Verificar si todas las edades están completas
      const allAgesFilled = newAges.every((age) => age && age.edad > 0);

      // Limpiar error si todas las edades están completas
      if (allAgesFilled) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors.childrenAges;
          return newErrors;
        });
      }

      return { ...prev, childrenAges: newAges };
    });
  }, []);

  /**
   * Valida el formulario completo
   */
  const validate = useCallback(() => {
    const today = new Date();
    const newErrors = {};

    // Validar fechas
    if (!formData.checkIn) {
      newErrors.checkIn = "Selecciona la fecha de llegada";
    }

    if (!formData.checkOut) {
      newErrors.checkOut = "Selecciona la fecha de salida";
    }

    if (formData.checkIn && formData.checkOut) {
      if (isBefore(formData.checkIn, today)) {
        newErrors.checkIn = "La fecha de llegada no puede ser anterior a hoy";
      }

      if (!isAfter(formData.checkOut, formData.checkIn)) {
        newErrors.checkOut =
          "La fecha de salida debe ser posterior a la llegada";
      }
    }

    // Validar edades de niños
    if (formData.children > 0) {
      const missingAges = formData.childrenAges.some(
        (age) =>
          age === null ||
          age.edad === undefined ||
          age.edad === "" ||
          age.edad === 0,
      );

      if (missingAges) {
        newErrors.childrenAges =
          "Por favor, ingrese la edad de todos los niños";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Resetea el formulario
   */
  const reset = useCallback(() => {
    setFormData({
      checkIn: null,
      checkOut: null,
      adults: 1,
      children: 0,
      childrenAges: [],
    });
    setErrors({});
  }, []);

  /**
   * Obtiene el texto formateado de las edades de los niños
   */
  const getChildrenAgesText = useCallback(() => {
    return formData.childrenAges
      .map(({ edad, unidad }) => `${edad} ${unidad}`)
      .join(", ");
  }, [formData.childrenAges]);

  /**
   * Obtiene los datos formateados para enviar al servidor
   */
  const getFormattedData = useCallback(() => {
    return {
      fechasalida: formData.checkIn
        ? format(formData.checkIn, "yyyy-MM-dd")
        : "",
      fecharegreso: formData.checkOut
        ? format(formData.checkOut, "yyyy-MM-dd")
        : "",
      adultos: formData.adults,
      ninos: formData.children,
      edad_niños: getChildrenAgesText(),
    };
  }, [formData, getChildrenAgesText]);

  return {
    formData,
    errors,
    setDateRange,
    setAdults,
    setChildren,
    setChildAge,
    validate,
    reset,
    getFormattedData,
    getChildrenAgesText,
  };
};
